import "./styles/App.css";
import React, { useContext } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Route,
  Navigate,
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
} from "react-router-dom";
import Home from "./pages/HomePage/HomePage";
import { MyDocumentPage } from "./pages/MyDocumentPage";
import { MyDocumentPageLoader } from "./pages/MyDocumentPage/loaders/MyDocumentPageLoader";
import { SignUpPage } from "./pages/SignUpPage";
import { AmenityPagesRoutes } from "./pages/AmenityPages/routes";
import {
  SignUpPageAction,
  SignUpPageErrorBoundary,
  SignUpPageLoader,
} from "./pages/SignUpPage/loaders";
import { ShadowInvitePage } from "./pages/ShadowInvitePage/ShadowInvitePage";
import {
  ShadowInvitePageAction,
  ShadowInvitePageLoader,
} from "./pages/ShadowInvitePage/loaders/ShadowInvitePageLoader";
import { ContactManagementPage } from "./pages/ContactManagementPage";
import {
  ContactManagementPageErrorBoundary,
  ContactManagementPageLoader,
} from "./pages/ContactManagementPage/loaders";
import { ReviewPage } from "./pages/ReviewPage";
import {
  ReviewPageAction,
  ReviewPageErrorBoundary,
  ReviewPageLoader,
} from "./pages/ReviewPage/loaders";
import { PropertyPagesRoutes } from "./pages/PropertyPages/routes";
import { UnitPagesRoutes } from "./pages/UnitPages/routes";
import { LeasePagesRoutes } from "./pages/LeasePages/routes";
import { BuildingPagesRoutes } from "./pages/BuildingPages/routes";
import { ProtectedRoute } from "./context/ProtectedRoute";
import * as APITypes from "~/API";
import { userContext } from "./context/ContextProvider";
import { BackgroundCheckPage } from "./pages/BackgroundCheckPage";
import {
  BackgroundCheckPageAction,
  BackgroundCheckPageErrorBoundary,
  BackgroundCheckPageLoader,
} from "./pages/BackgroundCheckPage/loaders";
import { MessageTablePageLoader } from "./pages/MessageTablePage/loaders/MessageTablePageLoader";
import { MessageTablePage } from "./pages/MessageTablePage";
import { ServiceRequestPagesRoutes } from "./pages/ServiceRequestPages/routes";
import { MessageDetailsPageLoader } from "./pages/MessageDetailsPage/loaders";
import { MessageDetailsPage } from "./pages/MessageDetailsPage/MessageDetailsPage";
import { ROUTES } from "./utils";
import { TenantAppShell } from "./components";
import { HomePageLoader } from "./pages/HomePage/loaders";
import {
  PaymentPageAction,
  PaymentPageLoader,
} from "./pages/PaymentPage/loaders";
import { PaymentPage } from "./pages/PaymentPage/PaymentPage";
import {
  PaymentTablePageAction,
  PaymentTablePageErrorBoundary,
  PaymentTablePageLoader,
} from "./pages/PaymentTablePage/loaders";
import { PaymentTablePage } from "./pages/PaymentTablePage/PaymentTablePage";
import {
  homeRouteHandle,
  paymentTableRouteHandle,
} from "./components/Layout/Fallbacks/routeContentFallbackRegistry";
import { RouterFallback } from "./components/Layout/Fallbacks";

const App: React.FC = () => {
  const queryClient = new QueryClient();
  const {
    status: _status,
    authenticated,
    isDoneWithBackground,
  }: {
    status: APITypes.TenantStatusType;
    authenticated: boolean;
    isDoneWithBackground: boolean;
  } = useContext(userContext);

  const routesJSX = (
    <Route>
      <Route
        path={ROUTES.signup}
        loader={SignUpPageLoader}
        action={SignUpPageAction}
        ErrorBoundary={SignUpPageErrorBoundary}
        element={
          !authenticated ? (
            <SignUpPage />
          ) : (
            <Navigate to="/" replace={true} />
          )
        }
      />

      <Route element={<TenantAppShell />}>
        {AmenityPagesRoutes()}
        {PropertyPagesRoutes()}
        {UnitPagesRoutes()}
        {BuildingPagesRoutes()}
        {LeasePagesRoutes()}
        {ServiceRequestPagesRoutes()}

        <Route
          path={ROUTES.paymentsPage}
          loader={PaymentPageLoader}
          action={PaymentPageAction}
          element={
            <ProtectedRoute roles={[APITypes.TenantStatusType.Active]}>
              <PaymentPage />
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.paymentTablePage}
          loader={PaymentTablePageLoader}
          action={PaymentTablePageAction}
          handle={paymentTableRouteHandle}
          ErrorBoundary={PaymentTablePageErrorBoundary}
          element={
            <ProtectedRoute
              roles={[
                APITypes.TenantStatusType.Active,
                APITypes.TenantStatusType.InActive,
              ]}
            >
              <PaymentTablePage />
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.shadowInvite}
          loader={ShadowInvitePageLoader}
          action={ShadowInvitePageAction}
          element={<ShadowInvitePage />}
        />

        <Route
          path={ROUTES.backgroundCheck}
          loader={BackgroundCheckPageLoader}
          action={BackgroundCheckPageAction}
          ErrorBoundary={BackgroundCheckPageErrorBoundary}
          element={
            authenticated && !isDoneWithBackground ? (
              <BackgroundCheckPage />
            ) : (
              <Navigate to="/" replace={true} />
            )
          }
        />

        <Route
          path={ROUTES.unauthorized}
          loader={HomePageLoader}
          element={
            <>
              <h1>Unauthorized</h1>
              <p>
                Sorry, you are not authorized to access this page. Please try a
                different account or a different page.
              </p>
            </>
          }
        />

        <Route
          path={ROUTES.home}
          loader={HomePageLoader}
          handle={homeRouteHandle}
          element={
            <ProtectedRoute
              roles={[
                APITypes.TenantStatusType.Active,
                APITypes.TenantStatusType.InActive,
              ]}
            >
              <Home />
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.help}
          loader={ContactManagementPageLoader}
          ErrorBoundary={ContactManagementPageErrorBoundary}
          element={
            <ProtectedRoute
              roles={[
                APITypes.TenantStatusType.Active,
                APITypes.TenantStatusType.InActive,
              ]}
            >
              <ContactManagementPage />
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.myDocumentsPage}
          loader={MyDocumentPageLoader}
          element={
            <ProtectedRoute roles={[APITypes.TenantStatusType.Active]}>
              <MyDocumentPage />
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.reviewPage}
          loader={ReviewPageLoader}
          action={ReviewPageAction}
          ErrorBoundary={ReviewPageErrorBoundary}
          element={
            <ProtectedRoute roles={[APITypes.TenantStatusType.Active]}>
              <ReviewPage />
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.messageTablePage}
          loader={MessageTablePageLoader}
          element={
            <ProtectedRoute roles={[APITypes.TenantStatusType.Active]}>
              <MessageTablePage />
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.messageDetails}
          loader={MessageDetailsPageLoader}
          element={
            <ProtectedRoute roles={[APITypes.TenantStatusType.Active]}>
              <MessageDetailsPage />
            </ProtectedRoute>
          }
        />
      </Route>
    </Route>
  );

  const routes = createRoutesFromElements(routesJSX);
  const router = createBrowserRouter(routes);

  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} fallbackElement={<RouterFallback />} />
    </QueryClientProvider>
  );
};

export default App;
